import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { debeGuardar, describirAntiguedad, hayBorradorRecuperable, } from './autosave.js';
/** Espera tras la última tecla antes de guardar. Suficiente para no escribir
 *  por pulsación, y corto para que cerrar la pestaña no pierda casi nada. */
const AUTOSAVE_DEBOUNCE_MS = 2000;
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FlowSchema } from '@zengine/flow-schema';
import { FlowVisualEditor } from './FlowVisualEditor.js';
import { FlowPreview } from './FlowPreview.js';
const MonacoEditor = lazy(async () => {
    const mod = await import('@monaco-editor/react');
    return { default: mod.default };
});
const DEFAULT_LABELS = {
    loading: 'Cargando…',
    tabVisual: 'Visual',
    tabJson: 'JSON',
    tabPreview: '▶ Preview',
    publish: 'Publicar',
    invalidJson: 'JSON inválido',
    schemaError: 'Errores de schema',
    visualNeedsData: 'Necesitás un flow cargado para usar el editor visual.',
    flowNotFoundTitle: 'No hay flow publicado todavía para este canal.',
    createScaffoldButton: 'Crear flow con scaffold mínimo',
};
export function FlowEditor({ flowId, tenantSlug, api, disableEditor = false, labels: labelsProp, }) {
    const labels = useMemo(() => ({ ...DEFAULT_LABELS, ...labelsProp }), [labelsProp]);
    const queryClient = useQueryClient();
    const [tab, setTab] = useState('visual');
    const { data, isLoading, error } = useQuery({
        queryKey: ['flow', tenantSlug, flowId],
        queryFn: () => api.getFlow(flowId, tenantSlug),
        enabled: Boolean(flowId && tenantSlug),
        retry: false,
    });
    const initial = useMemo(() => (data ? JSON.stringify(data, null, 2) : ''), [data]);
    const [draft, setDraft] = useState(null);
    // Resetea draft cuando cambia el flow (flowId o tenantSlug). Sin esto,
    // navegar entre flows mostraba el JSON del flow previo ("drift" P2 #11 QA).
    useEffect(() => {
        setDraft(null);
    }, [flowId, tenantSlug]);
    useEffect(() => {
        if (data && draft === null)
            setDraft(initial);
    }, [data, draft, initial]);
    const current = draft ?? initial;
    const ready = Boolean(data) && current.length > 0;
    // ── Autoguardado ────────────────────────────────────────────────────────
    // Hasta ahora el editor sólo sabía publicar: cerrar la pestaña antes de
    // hacerlo perdía el trabajo entero.
    const [ultimoGuardado, setUltimoGuardado] = useState(null);
    const [guardadoEn, setGuardadoEn] = useState(null);
    const [guardando, setGuardando] = useState(false);
    const [errorGuardado, setErrorGuardado] = useState(false);
    const [recuperable, setRecuperable] = useState(null);
    // Al abrir, buscamos si quedó trabajo sin publicar de una sesión anterior.
    useEffect(() => {
        if (!api.getDraft || !data)
            return;
        let vigente = true;
        void api
            .getDraft(flowId)
            .then((guardado) => {
            if (!vigente || !guardado)
                return;
            const texto = JSON.stringify(guardado.draft, null, 2);
            if (hayBorradorRecuperable({ borrador: texto, publicado: initial })) {
                setRecuperable({ texto, savedAt: guardado.savedAt });
            }
        })
            // Que falle la búsqueda del borrador no puede impedir editar.
            .catch(() => undefined);
        return () => {
            vigente = false;
        };
    }, [api, data, flowId, initial]);
    // Guardado con espera: el editor emite un cambio por pulsación.
    useEffect(() => {
        if (!api.saveDraft || !ready)
            return;
        if (!debeGuardar({ actual: current, publicado: initial, ultimoGuardado }))
            return;
        const t = setTimeout(() => {
            setGuardando(true);
            setErrorGuardado(false);
            let contenido;
            try {
                contenido = JSON.parse(current);
            }
            catch {
                // JSON a medio escribir: no se guarda, pero tampoco se avisa nada —
                // el error de sintaxis ya se muestra aparte.
                setGuardando(false);
                return;
            }
            void api
                .saveDraft(flowId, contenido)
                .then((res) => {
                setUltimoGuardado(current);
                setGuardadoEn(res.savedAt);
            })
                .catch(() => setErrorGuardado(true))
                .finally(() => setGuardando(false));
        }, AUTOSAVE_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [api, current, initial, ready, ultimoGuardado, flowId]);
    const validation = useMemo(() => (ready ? validateFlow(current) : null), [ready, current]);
    const mutation = useMutation({
        mutationFn: async () => {
            if (!validation || !validation.ok)
                throw new Error(labels.schemaError);
            return api.publishFlow(flowId, validation.value);
        },
        onSuccess: () => {
            // Publicado: el borrador dejó de representar trabajo pendiente.
            setUltimoGuardado(null);
            setGuardadoEn(null);
            setRecuperable(null);
            queryClient.invalidateQueries({ queryKey: ['flow', tenantSlug, flowId] });
        },
    });
    const createMutation = useMutation({
        mutationFn: () => api.createScaffold(flowId, tenantSlug),
        onSuccess: (created) => {
            queryClient.setQueryData(['flow', tenantSlug, flowId], created);
            setDraft(JSON.stringify(created, null, 2));
        },
    });
    const notFound = Boolean(error) &&
        /404|not.?found|no active flow/i.test(String(error?.message ?? ''));
    return (_jsxs("div", { className: "flex h-full flex-col", children: [_jsxs("div", { className: "mb-3 flex items-center justify-between", children: [_jsxs("h2", { className: "text-xl font-semibold", children: ["Editor de flow \u00B7 ", _jsx("span", { className: "font-mono text-base", children: flowId })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("div", { className: "inline-flex overflow-hidden rounded border border-slate-300 text-xs", children: ['visual', 'json', 'preview'].map((k) => (_jsx("button", { type: "button", onClick: () => setTab(k), className: `px-3 py-1 ${tab === k ? 'bg-brand-500 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`, children: k === 'visual' ? labels.tabVisual : k === 'json' ? labels.tabJson : labels.tabPreview }, k))) }), api.saveDraft && (_jsx("span", { "data-testid": "autosave-status", className: `text-xs ${errorGuardado ? 'text-amber-700' : 'text-slate-500'}`, children: guardando
                                    ? 'guardando…'
                                    : errorGuardado
                                        ? 'sin guardar'
                                        : guardadoEn
                                            ? `borrador ${describirAntiguedad(guardadoEn)}`
                                            : '' })), _jsx("button", { type: "button", "data-testid": "publish-btn", disabled: !validation || !validation.ok || mutation.isPending, onClick: () => mutation.mutate(), className: "rounded bg-brand-500 px-3 py-1 text-sm text-white disabled:opacity-50", children: labels.publish })] })] }), recuperable && (_jsxs("div", { "data-testid": "draft-recovery", className: "mb-2 flex items-center justify-between gap-3 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900", children: [_jsxs("span", { children: ["Qued\u00F3 trabajo sin publicar de ", describirAntiguedad(recuperable.savedAt), "."] }), _jsxs("span", { className: "flex gap-2", children: [_jsx("button", { type: "button", "data-testid": "draft-restore", onClick: () => {
                                    setDraft(recuperable.texto);
                                    setRecuperable(null);
                                }, className: "rounded bg-amber-600 px-2 py-1 text-white hover:bg-amber-700", children: "Recuperar" }), _jsx("button", { type: "button", "data-testid": "draft-discard", onClick: () => {
                                    setRecuperable(null);
                                    void api.discardDraft?.(flowId).catch(() => undefined);
                                }, className: "rounded border border-amber-400 px-2 py-1 hover:bg-amber-100", children: "Descartar" })] })] })), validation && !validation.ok && (_jsxs("div", { role: "alert", "data-testid": "flow-errors", className: "mb-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800", children: [_jsxs("strong", { children: [validation.kind === 'json' ? labels.invalidJson : labels.schemaError, ":"] }), _jsx("ul", { className: "mt-1 list-inside list-disc", children: validation.errors.map((e, i) => (_jsx("li", { children: e }, i))) })] })), isLoading && _jsx("p", { className: "text-slate-500", children: labels.loading }), error && !notFound && (_jsx("p", { className: "mb-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800", children: error.message })), notFound && !data && (_jsxs("div", { "data-testid": "flow-empty-create", className: "mb-3 rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-center", children: [_jsx("p", { className: "text-sm text-slate-600", children: labels.flowNotFoundTitle }), _jsx("button", { type: "button", "data-testid": "create-scaffold-btn", disabled: createMutation.isPending, onClick: () => createMutation.mutate(), className: "mt-3 rounded bg-brand-500 px-4 py-2 text-sm text-white hover:bg-brand-600 disabled:opacity-50", children: createMutation.isPending ? 'Creando…' : labels.createScaffoldButton }), createMutation.error && (_jsx("p", { className: "mt-2 text-xs text-red-700", children: createMutation.error.message }))] })), !disableEditor && tab === 'json' && (_jsx(Suspense, { fallback: _jsx("div", { className: "flex-1 flex items-center justify-center rounded border border-slate-200 bg-slate-50", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "inline-block h-8 w-8 animate-spin rounded-full border-3 border-slate-300 border-t-brand-500" }), _jsx("p", { className: "mt-2 text-xs text-slate-500", children: "Cargando editor JSON (Monaco)\u2026" })] }) }), children: _jsx("div", { className: "flex-1 overflow-hidden rounded border border-slate-200", children: _jsx(MonacoEditor, { height: "100%", defaultLanguage: "json", value: current, onChange: (v) => setDraft(v ?? ''), options: { minimap: { enabled: false }, fontSize: 13 } }) }) })), !disableEditor && tab === 'visual' && ready && (_jsx("div", { className: "flex-1 overflow-hidden rounded border border-slate-200 bg-slate-50", children: _jsx(FlowVisualEditor, { value: current, onChange: (v) => setDraft(v) }) })), !disableEditor && tab === 'visual' && !ready && !isLoading && (_jsx("div", { className: "flex-1 rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500", children: labels.visualNeedsData })), !disableEditor && tab === 'preview' && ready && (_jsx("div", { className: "flex-1 overflow-hidden rounded border border-slate-200 bg-slate-50 p-3", children: _jsx(FlowPreview, { flowJson: current }) })), !disableEditor && tab === 'preview' && !ready && !isLoading && (_jsx("div", { className: "flex-1 rounded border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500", children: "Necesit\u00E1s un flow cargado para usar el preview." })), disableEditor && (_jsx("textarea", { "data-testid": "flow-textarea", "aria-label": "flow json", value: current, onChange: (e) => setDraft(e.target.value), className: "h-72 w-full rounded border border-slate-200 p-2 font-mono text-xs" }))] }));
}
export function validateFlow(text) {
    if (!text.trim())
        return { ok: false, kind: 'json', errors: ['empty'] };
    let parsed;
    try {
        parsed = JSON.parse(text);
    }
    catch (err) {
        return { ok: false, kind: 'json', errors: [err.message] };
    }
    const result = FlowSchema.safeParse(parsed);
    if (!result.success) {
        return {
            ok: false,
            kind: 'schema',
            errors: result.error.issues.map((i) => `${i.path.join('.') || '<root>'}: ${i.message}`),
        };
    }
    return { ok: true, value: result.data };
}
//# sourceMappingURL=FlowEditor.js.map